import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_BackgroundColorChanger = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/background-color-changer',
			name: 'Background Color Changer — Color The Background Online | Lift',
			description:
				'Change background color of image instantly with AI. No watermark. Upload photo, pick any color, and transform your image background in seconds. Try now!',
			url: 'https://liftapp.ai/background-color-changer',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/background-color-changer#product' },
				{ '@id': 'https://liftapp.ai/background-color-changer#howto' },
				{ '@id': 'https://liftapp.ai/background-color-changer#benefits' },
				{ '@id': 'https://liftapp.ai/background-color-changer#faq' },
				{ '@id': 'https://liftapp.ai/background-color-changer#why-lift' },
				{ '@id': 'https://liftapp.ai/background-color-changer#explore-tools' },
				{ '@id': 'https://liftapp.ai/background-color-changer#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/background-color-changer#product',
			name: 'Background Color Changer Online',
			description:
				'Change image background color in seconds with AI. Get professional results with any color you choose, no watermarks, batch up to 20 images.',
			url: 'https://liftapp.ai/background-color-changer',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/background-color-changer#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift changes your image background color automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/background-color-changer#benefits',
			name: 'Who can benefit from Background Color Changer?',
			description:
				"Lift's AI Background Color Changer is useful for content creators, marketers, businesses, and professionals who want to customize image backgrounds with any color for branding, marketing, and visual content.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Create stunning visuals that match your brand aesthetics or trending color schemes instantly. Generate multiple color variants for thumbnails, Instagram posts, and TikTok content without any design skills.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Marketers & SMM managers — Run A/B tests with different background colors to maximize engagement and conversions. Create campaign-ready visuals faster by adapting one image to multiple brand guidelines and platform requirements.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Small business owners — Build professional product catalogs with consistent, clean background colors that boost customer trust. Save time and money by creating e-commerce photos in-house without expensive studios or designers.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Freelancers — Deliver client projects faster with instant background color customization for presentations and marketing materials. Streamline your workflow and take on more projects without learning complex photo editing software.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Create polished presentations, posters, and school projects with perfectly colored backgrounds. Transform personal photos and creative assignments into professional-looking visuals in just a few clicks.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Color the background',
				description:
					'Example of how Lift Background Color Changer transforms image backgrounds with custom colors for various use cases.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/background-color-changer#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Background Color Changer?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Background Color Changer is a premium online tool that instantly replaces your image background with any color of your choice with pixel-perfect precision — ideal for product photos, social media content, or graphic design.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Background Color Changer work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload your photo to Lift's background color change tool. Our AI automatically detects the subject and replaces the background with your chosen color in seconds — no photo editing experience needed. Download up to 20 images with your custom background colors.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift Background Color Changer uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking results without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/background-color-changer#why-lift',
			name: "Why Lift's AI Background Color Changer stands out",
			description:
				"Lift's Background Color Changer combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Change background color',
				description:
					"Comparison of Lift's AI Background Color Changer benefits — professional quality, AI accuracy, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/background-color-changer#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/background-color-changer#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Background Color Changer',
					item: 'https://liftapp.ai/background-color-changer',
				},
			],
		},
	],
};
